import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  OnDestroy,
} from '@angular/core';

/**
 * Directive to inject custom styles into goab-pagination shadow DOM.
 *
 * Usage:
 * 1. Apply to individual pagination components:
 *    <goab-pagination paginationShadowStyle>...</goab-pagination>
 *
 * 2. Apply with custom styles:
 *    <goab-pagination [paginationShadowStyle]="customCss">...</goab-pagination>
 *
 * Default styles force pagination icons/arrows to be visible (fixes shadow DOM visibility issues).
 */
@Directive({
  selector: '[paginationShadowStyle]',
  standalone: true,
})
export class PaginationShadowStyleDirective
  implements AfterViewInit, OnDestroy
{
  private static readonly STYLE_ID = 'pagination-shadow-custom-styles';
  private mutationObserver?: MutationObserver;

  /**
   * Custom CSS to inject. If not provided, uses default icon visibility styles.
   */
  @Input() paginationShadowStyle?: string;

  private readonly defaultStyles = `
    goa-icon,
    svg,
    .arrow,
    .pagination-arrow,
    .prev,
    .next,
    [class*='icon'],
    [class*='arrow'],
    [class*='prev'],
    [class*='next'] {
      display: inline-flex !important;
      visibility: visible !important;
      opacity: 1 !important;

    }

    button,
    .pagination-button {
      display: inline-flex !important;
      visibility: visible !important;
      opacity: 1 !important;
    }
  `;

  private readonly iconStyles = `
    svg,
    path,
    [class*='icon'] {
      display: inline-flex !important;
      visibility: visible !important;
      opacity: 1 !important;
      color: #333 !important;
      fill: #333 !important;
    }
  `;

  private readonly dropdownStyles = `
    goa-icon,
    svg,
    path,
    .chevron,
    .dropdown-icon,
    [class*='icon'],
    [class*='chevron'] {
      display: inline-flex !important;
      visibility: visible !important;
      opacity: 1 !important;
      color: #333 !important;
      fill: #333 !important;
    }
  `;

  constructor(private el: ElementRef<HTMLElement>) {}

  ngAfterViewInit(): void {
    // Give web component time to render its shadow DOM
    setTimeout(() => this.applyStyles(), 100);
    // Retry after a longer delay to catch late-rendered icons
    setTimeout(() => this.applyStyles(), 500);

    // Watch for shadow DOM changes (component might re-render)
    this.setupMutationObserver();
  }

  ngOnDestroy(): void {
    this.mutationObserver?.disconnect();
  }

  private applyStyles(): void {
    const element = this.el.nativeElement;

    // Find the goa-pagination inside goab-pagination (Angular wrapper around web component)
    const goaPagination = element.querySelector('goa-pagination');

    const targetElement = goaPagination || element;

    const shadowRoot = (
      targetElement as HTMLElement & { shadowRoot: ShadowRoot | null }
    ).shadowRoot;

    if (shadowRoot) {
      this.injectStylesIntoShadowRoot(shadowRoot);

      // Style nested elements with their own shadow DOM
      this.styleNestedElements(shadowRoot);

      // Style previous and next buttons
      this.buttonStyles(shadowRoot);
    }
  }

  private buttonStyles(parentShadowRoot: ShadowRoot): void {
    const previousShadowRoot = parentShadowRoot.querySelector(
      'goa-block > div > goa-block[data-testid="page-links"] > goa-button:nth-of-type(1)'
    )?.shadowRoot;
    if (previousShadowRoot) {
      const buttonHtml = previousShadowRoot.querySelector(
        'button'
      ) as HTMLElement;
      if (buttonHtml) {
        buttonHtml.style.width = '150px';
      }
    }

    const nextShadowRoot = parentShadowRoot.querySelector(
      'goa-block > div > goa-block[data-testid="page-links"] > goa-button:nth-of-type(2)'
    )?.shadowRoot;
    if (nextShadowRoot) {
      const buttonHtml = nextShadowRoot.querySelector('button') as HTMLElement;
      if (buttonHtml) {
        buttonHtml.style.width = '150px';
      }
    }
  }

  /**
   * Style nested web components that have their own shadow DOM.
   * Focus on goa-dropdown elements inside pagination.
   */
  private styleNestedElements(parentShadowRoot: ShadowRoot): void {
    // Style goa-dropdown elements (page size selector)
    this.styleGoaDropdowns(parentShadowRoot);

    // Style goa-icon elements directly in the pagination shadow root
    this.styleGoaIcons(parentShadowRoot);

    // Also check goa-block elements which may contain dropdowns
    const blocks = parentShadowRoot.querySelectorAll('goa-block');
    blocks.forEach((block) => {
      // Look for goa-dropdown inside the block's light DOM (slotted content)
      const slotDropdowns = block.querySelectorAll('goa-dropdown');
      slotDropdowns.forEach((dropdown) => {
        this.styleGoaDropdownElement(dropdown as HTMLElement);
      });

      // Also check for icons directly in blocks
      const blockIcons = block.querySelectorAll('goa-icon');
      blockIcons.forEach((icon) => {
        this.styleGoaIconElement(icon as HTMLElement);
      });
    });
  }

  /**
   * Style goa-dropdown elements within a shadow root
   */
  private styleGoaDropdowns(shadowRoot: ShadowRoot): void {
    const dropdowns = shadowRoot.querySelectorAll('goa-dropdown');
    dropdowns.forEach((dropdown) => {
      this.styleGoaDropdownElement(dropdown as HTMLElement);
    });
  }

  /**
   * Style a single goa-dropdown element - inject styles into its shadow DOM
   */
  private styleGoaDropdownElement(dropdown: HTMLElement): void {
    const dropdownShadowRoot = (
      dropdown as HTMLElement & { shadowRoot: ShadowRoot | null }
    ).shadowRoot;
    if (dropdownShadowRoot) {
      this.injectStylesIntoShadowRoot(
        dropdownShadowRoot,
        this.dropdownStyles,
        'dropdown'
      );

      // Also style any goa-icon inside the dropdown's shadow root
      this.styleGoaIcons(dropdownShadowRoot);

      // Check for icons in the dropdown's light DOM too
      const slottedIcons = dropdown.querySelectorAll('goa-icon');
      slottedIcons.forEach((icon) => {
        this.styleGoaIconElement(icon as HTMLElement);
      });
    }
  }

  /**
   * Style a single goa-icon element
   */
  private styleGoaIconElement(icon: HTMLElement): void {
    const iconShadowRoot = (
      icon as HTMLElement & { shadowRoot: ShadowRoot | null }
    ).shadowRoot;
    if (iconShadowRoot) {
      this.injectStylesIntoShadowRoot(iconShadowRoot, this.iconStyles, 'icon');
    }
  }

  /**
   * Style goa-icon elements within a shadow root
   */
  private styleGoaIcons(shadowRoot: ShadowRoot): void {
    const icons = shadowRoot.querySelectorAll('goa-icon');
    icons.forEach((icon) => {
      this.styleGoaIconElement(icon as HTMLElement);
    });
  }

  private injectStylesIntoShadowRoot(
    shadowRoot: ShadowRoot,
    styles?: string,
    suffix?: string
  ): void {
    const styleId = suffix
      ? `${PaginationShadowStyleDirective.STYLE_ID}-${suffix}`
      : PaginationShadowStyleDirective.STYLE_ID;

    // Avoid duplicate injection
    if (shadowRoot.querySelector(`#${styleId}`)) {
      return;
    }

    const styleElement = document.createElement('style');
    styleElement.id = styleId;
    styleElement.textContent =
      styles || this.paginationShadowStyle || this.defaultStyles;
    shadowRoot.appendChild(styleElement);
  }

  private setupMutationObserver(): void {
    this.mutationObserver = new MutationObserver(() => {
      this.applyStyles();
    });

    this.mutationObserver.observe(this.el.nativeElement, {
      childList: true,
      subtree: true,
    });
  }
}
